import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';

// Toast Context
const ToastContext = createContext(null);

// Toast types
const TOAST_TYPES = {
  success: {
    icon: CheckCircle,
    bgClass: 'bg-green-500',
    borderClass: 'border-green-400'
  },
  error: {
    icon: XCircle,
    bgClass: 'bg-red-500',
    borderClass: 'border-red-400'
  },
  info: {
    icon: AlertCircle,
    bgClass: 'bg-blue-500',
    borderClass: 'border-blue-400'
  }
};

// Single Toast Component - no useTheme dependency to avoid context boundary issues
const ToastItem = ({ id, message, type = 'success', onClose }) => {
  const config = TOAST_TYPES[type] || TOAST_TYPES.info;
  const Icon = config.icon;

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, 3000);
    return () => clearTimeout(timer);
  }, [id, onClose]);

  // Check dark mode from DOM class instead of context (since Toast renders outside ThemeProvider)
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  return (
    <div 
      className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border animate-slide-in-right ${
        isDark 
          ? 'bg-slate-800 border-slate-700 text-white' 
          : 'bg-white border-gray-200 text-gray-900'
      }`}
      style={{ minWidth: '280px', maxWidth: '400px' }}
    >
      <div className={`p-1 rounded-full ${config.bgClass}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <p className="flex-1 text-sm font-medium">{message}</p>
      <button 
        onClick={() => onClose(id)}
        className={`p-1 rounded-full transition-colors ${
          isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'
        }`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Toast Container Component
export const ToastContainer = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = {
    success: (message) => addToast(message, 'success'),
    error: (message) => addToast(message, 'error'),
    info: (message) => addToast(message, 'info'),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Toast display area */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
        {toasts.map(t => (
          <ToastItem 
            key={t.id} 
            id={t.id}
            message={t.message} 
            type={t.type} 
            onClose={removeToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// Hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    // Fallback to alert if not in ToastContainer
    return {
      success: (msg) => console.log('Toast:', msg),
      error: (msg) => console.error('Toast:', msg),
      info: (msg) => console.log('Toast:', msg),
    };
  }
  return context;
};

export default ToastContainer;
